import { useState, useCallback, useEffect } from "react";
import { legalMeta } from "@/content/legalContent";

export interface CookiePreferences {
  necessary: true;
  statistics: boolean;
  marketing: boolean;
  timestamp: string;
  version: number;
}

const STORAGE_KEY = legalMeta.consentStorageKey;

function readStored(): CookiePreferences | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.version === legalMeta.consentVersion) return parsed;
    return null;
  } catch {
    return null;
  }
}

function persist(prefs: CookiePreferences) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
}

export function useCookieConsent() {
  const [preferences, setPreferences] = useState<CookiePreferences | null>(
    () => readStored()
  );
  const [showBanner, setShowBanner] = useState(() => !readStored());
  const [showSettings, setShowSettings] = useState(false);

  const hasConsented = preferences !== null;

  const save = useCallback((prefs: CookiePreferences) => {
    persist(prefs);
    setPreferences(prefs);
    setShowBanner(false);
    setShowSettings(false);
  }, []);

  const acceptAll = useCallback(() => {
    save({
      necessary: true,
      statistics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
      version: legalMeta.consentVersion,
    });
  }, [save]);

  const acceptNecessary = useCallback(() => {
    save({
      necessary: true,
      statistics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
      version: legalMeta.consentVersion,
    });
  }, [save]);

  const savePreferences = useCallback(
    (stats: boolean, marketing: boolean) => {
      save({
        necessary: true,
        statistics: stats,
        marketing,
        timestamp: new Date().toISOString(),
        version: legalMeta.consentVersion,
      });
    },
    [save]
  );

  const openSettings = useCallback(() => setShowSettings(true), []);
  const closeSettings = useCallback(() => setShowSettings(false), []);

  return {
    preferences,
    hasConsented,
    showBanner,
    showSettings,
    acceptAll,
    acceptNecessary,
    savePreferences,
    openSettings,
    closeSettings,
  };
}
